import type { SeedSpeciesProductionFields } from './IGlobalCatalogue';
import type { IInputWithholdPeriodReference, IItem, IUnit } from './IInput';

export enum InputStepType {
  Reserved = 0,
  Generic = 1,
  Application = 100, // Application (Logical)
  CropProtection = 101, // Crop Protection (Chemical)
  Adjuvant = 102,
  SoilInput = 103, // Soil Input (Fertiliser)
  Biostimulant = 104,
  Production = 201,
  Seed = 301,
  SeedDress = 302,
}

export interface IGcProductGroup {
  productGroupId: string | null;
  snapProductGroup: string | null;
}

export interface IActiveConstituent {
  name: string;
  concentration: number | null;
  concentrationUnit: IUnit | null;
  moaGroupId: string | null;
  snapMoaGroup: string | null;
}

export interface IInputStepBase {
  dateCreated: string;
  creatorId: string | null;
  snapCreator: string | null;
  dateModified: string;
  modifierId: string | null;
  snapModifier: string | null;
  startDate: string | null;
  endDate: string | null;
  area: number | null;
  areaUnit: IUnit | null;
  timing: IItem | null;
  notes: string | null;
  stepId: string;
  stepType: InputStepType;
  item: string;
  globalId: string | null;
}

export interface IInputStepRateCost extends IInputStepBase {
  rate: number | null;
  appUnit: IUnit | null;
  cost: number | null;
}

export interface IInputStepCo2eFields {
  co2eScope1: number | null;
  co2eScope2: number | null;
  co2eScope3: number | null;
  co2eUnit: IUnit | null;
}

export interface IInputStepManufacturerSupplierFields {
  manufacturerId: string | null;
  snapManufacturer: string | null;
  supplierId: string | null;
  snapSupplier: string | null;
}

export type PoisonSchedule = 0 | 1 | 2 | 3 | 4 | 5;
export const DEFAULT_POISON_SCHEDULE: PoisonSchedule = 0;

export interface IInputStepPlantProtectionFields
  extends IInputStepManufacturerSupplierFields {
  productGroups: IGcProductGroup[];
  activeConstituents?: IActiveConstituent[];
  formulationCode: string | null;
  formulation: string | null;
  apvmaNumber: string | null;
  poisonSchedule: PoisonSchedule;
  withholdPeriods: IInputWithholdPeriodReference[];
  reentry: number | null;
  reentryUnit: IUnit | null;
  reentryNotes: string | null;
  batchNumber: string | null;
}

export interface IInputStepGeneric extends IInputStepBase {
  stepType: InputStepType.Generic;
}

export interface IInputStepApplication
  extends IInputStepRateCost,
    IInputStepCo2eFields {
  stepType: InputStepType.Application;
}

export interface IInputStepCropProtection
  extends IInputStepRateCost,
    IInputStepPlantProtectionFields {
  stepType: InputStepType.CropProtection;
}

export interface IInputStepAdjuvant
  extends IInputStepRateCost,
    IInputStepPlantProtectionFields {
  stepType: InputStepType.Adjuvant;
}

export interface IInputStepSoilInput
  extends Omit<IInputStepApplication, 'stepType'>,
    IInputStepManufacturerSupplierFields {
  stepType: InputStepType.SoilInput;
  techStackId: string | null;
  snapTechStack: string | null;
  productGroups: IGcProductGroup[];
  nutrientN: number | null;
  nutrientP: number | null;
  nutrientK: number | null;
  nutrientS: number | null;
  nutrientCa: number | null;
  nutrientMg: number | null;
  nutrientNa: number | null;
  nutrientB: number | null;
  nutrientCu: number | null;
  nutrientFe: number | null;
  nutrientMn: number | null;
  nutrientZn: number | null;
  nutrientMo: number | null;
  nutrientCl: number | null;
  nutrientSi: number | null;
  nutrientNi: number | null;
  nutrientCo: number | null;
  additive: string | null;
  isSeedDressing: boolean;
  isLiquid: boolean;
  bulkDensity: number | null;
  bulkDensityUnit: IUnit | null;
}

export interface IInputStepBiostimulant
  extends IInputStepRateCost,
    IInputStepManufacturerSupplierFields {
  stepType: InputStepType.Biostimulant;
  linkedStepId: string | null;
  actionModeId: string | null;
  snapActionMode: string | null;
  productGroups: IGcProductGroup[];
}

/** Values copied from the Input's crop/variety and its GSeedSpecies when a production step is created. */
export interface IInputStepProductionCropFields
  extends IInputStepManufacturerSupplierFields,
    SeedSpeciesProductionFields {
  cropId: string | null;
  snapCropName: string | null;
  varietyId: string | null;
  snapVarietyName: string | null;
  techStackId: string | null;
  snapTechStack: string | null;
}

export interface IInputStepProduction
  extends IInputStepBase,
    IInputStepProductionCropFields {
  stepType: InputStepType.Production;
  targetGrade: string;
  yieldLow: number | null;
  yield: number | null;
  yieldHigh: number | null;
  retain: number | null;
  yieldUnit: IUnit | null;
  priceLow: number | null;
  price: number | null;
  priceHigh: number | null;
  priceUnit: IUnit | null;
}

export interface IInputStepSeed
  extends IInputStepRateCost,
    IInputStepManufacturerSupplierFields {
  stepType: InputStepType.Seed;
  item2: string | null;
  globalId2: string | null;
  techStackId: string | null;
  snapTechStack: string | null;
}

export enum SeedDressRateMode {
  Per100Kg = 1,
  PerHa = 2,
}

export interface IInputStepSeedDress
  extends IInputStepRateCost,
    IInputStepPlantProtectionFields {
  stepType: InputStepType.SeedDress;
  linkedStepId: string | null;
  rateMode: SeedDressRateMode;
}

export type IInputStep =
  | IInputStepGeneric
  | IInputStepApplication
  | IInputStepCropProtection
  | IInputStepAdjuvant
  | IInputStepSoilInput
  | IInputStepBiostimulant
  | IInputStepProduction
  | IInputStepSeed
  | IInputStepSeedDress;
